import {
  FILE_BLOCK_RULE,
  FREE_SYSTEM_PROMPT,
  answerLanguageRule,
  askAgentDataFlowRules,
  askAgentInstructions,
  buildAskAppContextBlock,
  buildAskSystemPrompt,
  asksForRevision,
  asksForFile,
  buildAppContextBlock,
  buildSystemPrompt,
  buildUserPrompt,
  editSourcesNote,
  type EditSource,
} from "../api/prompt";
import { rewriteQuery } from "../api/queryRewrite";
import { EpflSearchUnavailable, searchEpflHybrid, searchEpflPolylex } from "../api/epflHybrid";
import { searchExaWeb } from "../api/exaWeb";
import { extractUrls, fetchWebPage, pageToDoc, unreadablePageDoc } from "../api/webPage";
import { AgentUnavailableError, runUltraChat, type UltraDeps } from "../api/ultraAgent";
import { embedQuery, streamChatCompletion, type ChatMessage } from "../api/rcpLLM";
import { composeContent, composeText, toAgentText, toLLMContent } from "../api/messageContent";
import * as conversations from "../db/conversations";
import * as documents from "../db/documents";
import * as messages from "../db/messages";
import { retrieve } from "../db/retrieval";
import type {
  ActivityStep,
  AttachedDoc,
  Message,
  RetrievalHit,
  RetrievedChunkSnapshot,
  StoredDocument,
} from "../db/types";
import { app, isDefaultConversationTitle } from "./app.svelte";
import { DATA_FLOW_USERS, docsFitFullText, fullTextBudget, lexOnlyActive, searchScope, TOP_K_MAX, TOP_K_MIN, type Config } from "./config";
import { hasDocxRevision, repairDocxRevisions } from "./docxRepair";
import { checkSigners, hasNamedSigner, type DirectoryLog } from "./signerCheck";
import { conversationFileBlocks } from "./originals";
import { i18n, tr } from "../i18n/i18n.svelte";
import { generateTitle } from "../api/conversationTitle";
import { senderContext } from "../api/epflDirectory.svelte";

const TITLE_MAX = 60;

function clampK(k: number): number {
  if (!Number.isFinite(k)) return 20;
  return Math.max(TOP_K_MIN, Math.min(TOP_K_MAX, Math.round(k)));
}

function titleFromQuestion(q: string): string {
  return q.length > TITLE_MAX ? q.slice(0, TITLE_MAX).trimEnd() + "…" : q;
}

/** Titre provisoire dès que la question est enregistrée : une réponse en
 *  erreur ne laisse plus de « Nouvelle conversation » (ou « New
 *  conversation ») dans l'historique. */
async function provisionalTitle(convId: string, text: string): Promise<void> {
  if (!text) return;
  const conv = await conversations.getConversation(convId);
  if (!conv || !isDefaultConversationTitle(conv.title)) return;
  conv.title = titleFromQuestion(text);
  await conversations.putConversation(conv);
  await app.refreshConversations();
}

/** Current date/time + timezone, injected into the system prompt so the model
 *  has a "now" reference for time-sensitive questions (stock prices, "latest",
 *  "today", current events). The IANA timezone (e.g. "Europe/Zurich") doubles
 *  as an approximate location without needing a geolocation permission. */
function temporalContext(): string {
  const now = new Date();
  let timeZone = "";
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
  } catch {
    timeZone = "";
  }
  let formatted: string;
  try {
    formatted = new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "full",
      timeStyle: "short",
      ...(timeZone ? { timeZone } : {}),
    }).format(now);
  } catch {
    formatted = now.toISOString();
  }
  const place = timeZone ? ` (fuseau horaire ${timeZone})` : "";
  return (
    `Contexte temporel : nous sommes actuellement le ${formatted}${place}. ` +
    `Utilise cette date et cette heure comme référence du « maintenant » pour ` +
    `toute question sensible au temps (cours de bourse, dernières actualités, ` +
    `« aujourd'hui », « actuel », âge, échéances, etc.).`
  );
}

function isAbortError(e: unknown): boolean {
  return (
    e instanceof DOMException && e.name === "AbortError"
  ) || (e instanceof Error && e.name === "AbortError");
}

export interface MessageAttachments {
  images?: string[];
  docs?: AttachedDoc[];
}

/** Enveloppe une recherche pour l'inscrire au journal d'activité : la requête
 *  exacte au départ, le nombre de résultats au retour. Même enveloppe pour le
 *  mode normal, la recherche itérative et les outils de l'agent Ultra. */
function tracked(
  source: "local" | "epfl" | "polylex" | "web",
  search: (q: string) => Promise<RetrievedChunkSnapshot[]>,
): (q: string) => Promise<RetrievedChunkSnapshot[]> {
  return async (q) => {
    const i = app.addActivity({ kind: "search", source, query: q });
    const step = () => app.activity[i] as Extract<ActivityStep, { kind: "search" }>;
    try {
      const res = await search(q);
      step().count = res.length;
      return res;
    } catch (e) {
      step().failed = true;
      // Recherche EPFL impossible (jeton refusé, serveur indisponible) : on le
      // DIT à l'usager au lieu d'un « 0 résultat » trompeur, et la réponse
      // continue avec les autres sources — ne pas relancer l'erreur.
      if (e instanceof EpflSearchUnavailable) {
        step().message = e.message;
        return [];
      }
      throw e;
    }
  };
}

/** La portée de la recherche telle que l'usager la lit, pour le prompt Ask
 *  EPFL (« Je n'ai pas trouvé cette information dans [portée] »). */
function scopeLabel(cfg: Config, hasDocs: boolean): string {
  const docs = "vos documents";
  switch (searchScope(cfg)) {
    case "docs":
      return hasDocs ? docs : "aucune source : discussion libre avec le modèle";
    case "polylex":
      return `Polylex (lois, ordonnances, règlements et directives de l'EPFL)${hasDocs ? ` et ${docs}` : ""}`;
    case "epfl":
      return `EPFL + mes documents (Polylex, le site epfl.ch et les bases de connaissances des services)${hasDocs ? ` et ${docs}` : ""}`;
    case "web":
      return `EPFL + mes documents et le web${hasDocs ? `, et ${docs}` : ""}`;
  }
}

function charsOf(docs: StoredDocument[]): number {
  return docs.reduce((sum, d) => sum + d.chunks.reduce((t, c) => t + c.text.length, 0), 0);
}

/** Documents interrogés à envoyer EN ENTIER plutôt que par passages retrouvés.
 *
 *  PolyChat (21.10.2026) : tous, tant qu'ils tiennent dans le budget de
 *  lecture intégrale — sur un extrait de recherche, le modèle passe à côté de
 *  ce qu'il n'a pas vu. Au-delà du budget, la recherche par passages reprend
 *  (la v1 garde son interrupteur FullText explicite).
 *
 *  Dans les deux interfaces, modifier un document exige de le lire en entier :
 *  les documents dont on garde l'original (.docx, PDF) et les PDF numérisés,
 *  à réécrire, s'ils tiennent, quand la question demande une modification. */
async function docsToReadWhole(question: string, cfg: Config): Promise<StoredDocument[]> {
  if (app.activeDocIds.length === 0) return [];
  const docs = await documents.getDocuments(app.activeDocIds);
  if (app.askEpfl && docsFitFullText(cfg, charsOf(docs))) return docs;
  if (!asksForRevision(question)) return [];
  const targets = docs.filter((d) => d.original || /\.pdf$/i.test(d.name));
  return targets.length > 0 && docsFitFullText(cfg, charsOf(targets)) ? targets : [];
}

/** Demande de modification : dit au modèle quels Word et PDF fournis
 *  (documents actifs, pièces jointes de la conversation) se modifient dans
 *  leur fichier d'origine et lesquels doivent être réécrits. */
async function revisionNote(question: string, attached: AttachedDoc[]): Promise<string> {
  if (!asksForRevision(question)) return "";
  const isPdf = (name: string) => /\.pdf$/i.test(name);
  const editableKind = (name: string) => /\.(docx|pdf)$/i.test(name);
  const seen = new Set<string>();
  const sources: EditSource[] = [];
  const add = (s: EditSource) => {
    if (seen.has(s.name)) return;
    seen.add(s.name);
    sources.push(s);
  };
  // Pièce jointe sans original : un PDF numérisé (keepsOriginal) ou trop gros.
  const fromAttached = (d: AttachedDoc) => {
    if (editableKind(d.name)) add({ name: d.name, editable: !!d.original, scanned: isPdf(d.name) && !d.original });
  };
  attached.forEach(fromAttached);
  for (const m of [...app.currentMessages].reverse()) (m.attachedDocs ?? []).forEach(fromAttached);
  if (app.activeDocIds.length > 0) {
    for (const d of await documents.getDocuments(app.activeDocIds)) {
      if (editableKind(d.name)) add({ name: d.name, editable: !!d.original, scanned: isPdf(d.name) && d.ocrUsed });
    }
  }
  return editSourcesNote(sources);
}

/** Tous les passages d'un document, dans l'ordre. */
function wholeDocSnapshots(doc: StoredDocument): RetrievedChunkSnapshot[] {
  return doc.chunks.map((chunk) => ({
    number: 0,
    docId: doc.id,
    chunkId: chunk.id,
    docName: doc.name,
    page: chunk.page,
    section: chunk.section,
    text: chunk.text,
    score: 1,
    source: "local",
  }));
}

/** Révision Word dans la réponse : essai à blanc sur le document d'origine,
 *  et recalage par le modèle des passages introuvables (state/docxRepair.ts),
 *  avant que la réponse soit enregistrée. */
async function verifyDocxRevisions(full: string, cfg: Config, signal: AbortSignal): Promise<string> {
  if (!hasDocxRevision(full)) return full;
  const i = app.addActivity({ kind: "notice", text: tr({ fr: "Vérification du fichier Word…", en: "Checking the Word file…" }) });
  const report = await repairDocxRevisions(full, cfg, signal);
  (app.activity[i] as Extract<ActivityStep, { kind: "notice" }>).text =
    report.summary ??
    tr({
      fr: "Vérification du fichier Word : toutes les modifications se placent dans le document.",
      en: "Word file checked: every change fits the document.",
    });
  app.pendingAssistant = report.content;
  return report.content;
}

/** Fichier au nom d'une personne que l'annuaire EPFL ne connaît pas sous ce
 *  nom : candidats proches, choix du modèle ou question à l'usager
 *  (state/signerCheck.ts), avant que la réponse soit enregistrée. */
async function verifySigners(full: string, question: string, cfg: Config, signal: AbortSignal): Promise<string> {
  if (!hasNamedSigner(full)) return full;
  const log: DirectoryLog = (query) => {
    const i = app.addActivity({ kind: "directory", query });
    return (result, failed) => {
      const step = app.activity[i] as Extract<ActivityStep, { kind: "directory" }>;
      step.result = result;
      step.failed = failed;
    };
  };
  const report = await checkSigners(full, question, cfg, signal, log);
  app.pendingAssistant = report.content;
  return report.content;
}

export async function sendUserMessage(
  text: string,
  attach?: MessageAttachments,
): Promise<void> {
  const trimmed = text.trim();
  // Rebuild as plain arrays/objects: the caller passes Svelte $state proxies,
  // which IndexedDB cannot structured-clone ("[object Array] could not be
  // cloned"). `.map` over a proxy returns an ordinary array; rebuilding the
  // docs strips the per-object proxy too.
  const images = (attach?.images ?? []).map((u) => u);
  const docs: AttachedDoc[] = (attach?.docs ?? []).map((d) => ({
    name: d.name,
    text: d.text,
    tokens: d.tokens,
    ...(d.original ? { original: d.original } : {}),
  }));
  const hasAttachments = images.length > 0 || docs.length > 0;
  if ((!trimmed && !hasAttachments) || !app.config || app.isStreaming) return;
  const cfg = app.config;

  app.lastError = null;
  app.lastWarning = null;
  app.isStreaming = true;
  app.activity = [];
  const startedAt = Date.now();
  app.turnStartedAt = startedAt;
  app.lastSignAt = startedAt;
  app.thinkingChars = 0;
  const controller = new AbortController();
  app.chatAbortController = controller;
  const signal = controller.signal;

  let convId: string | null = null;
  let retrieved: RetrievedChunkSnapshot[] = [];
  let full = "";
  let aborted = false;
  let agentFailed = false;
  /** Documents interrogés envoyés en entier dans ce tour (mode Rapide). */
  let wholeDocs: StoredDocument[] = [];

  try {
    convId = app.currentConversationId ?? (await app.newConversation());
    await app.rememberConversationSettings(convId);

    const userMsg: Message = {
      id: crypto.randomUUID(),
      conversationId: convId,
      role: "user",
      content: trimmed,
      ...(images.length > 0 ? { images } : {}),
      ...(docs.length > 0 ? { attachedDocs: docs } : {}),
      createdAt: Date.now(),
    };
    await messages.putMessage(userMsg);
    app.currentMessages = [...app.currentMessages, userMsg];
    if (app.askEpfl) await provisionalTitle(convId, trimmed || docs[0]?.name || "");

    // Pages web citées : lues en entier et jointes au message comme un document
    // glissé (elles restent donc dans l'historique pour les questions suivantes).
    const urls = extractUrls(trimmed);
    if (urls.length > 0) {
      for (const url of urls) {
        const i = app.addActivity({ kind: "page", url });
        const step = () => app.activity[i] as Extract<ActivityStep, { kind: "page" }>;
        try {
          const page = await fetchWebPage(url, cfg, signal);
          step().title = page.title;
          docs.push(pageToDoc(page));
        } catch (e) {
          if (signal.aborted) throw e;
          step().failed = true;
          docs.push(unreadablePageDoc(url, e instanceof Error ? e.message : String(e)));
        }
      }
      userMsg.attachedDocs = docs;
      await messages.putMessage(userMsg);
      app.currentMessages = app.currentMessages.map((m) => (m.id === userMsg.id ? { ...userMsg } : m));
    }

    const hasDocs = app.activeDocIds.length > 0;
    const bypassRag = hasDocs && app.bypassRag;
    // EPFL hybrid retrieval (site + ServiceNow KB + incidents, OBO). Works on
    // its own — even with no local document selected — so the user can query
    // EPFL/ServiceNow knowledge in "chat libre". Gated on opt-in + EPFL session.
    // Bulle LEX enfoncée : la recherche EPFL ne porte que sur Polylex, que la
    // bulle EPFL soit active ou non (c'est une source en soi pour les juristes).
    const lexOnly = lexOnlyActive(cfg);
    const epflSearch = lexOnly ? searchEpflPolylex : searchEpflHybrid;
    const wantHybrid = !bypassRag && (!!cfg.epflHybridEnabled || lexOnly) && !!app.oidcSession;
    // Exa web search (hosted MCP, keyless). Also works with no local document.
    const wantExa = !bypassRag && !!cfg.exaWebEnabled;
    const priorMessages = app.currentMessages.slice(0, -1);
    // Demande de modification : quels Word et PDF se modifient sur place.
    const editNote = await revisionNote(trimmed, docs);
    // Current date/time + timezone, shared by the gather loop and the answer
    // prompt so both reason from the same "now".
    const temporal = temporalContext();

    // ── Réflexion activée : l'agent (backend OpenHands) mène la recherche ──
    // Il choisit lui-même ses recherches parmi les sources activées ; les
    // outils s'exécutent ICI (les documents perso ne quittent le navigateur
    // que sous forme d'extraits, ou en entier via read_document si l'agent le
    // juge utile). Sans état : l'historique part avec la question. Si le
    // backend est injoignable, on retombe sur le circuit standard ci-dessous,
    // toujours en réflexion libre. Le `finally` partagé persiste la réponse.
    agentFailed = false;
    if (cfg.thinkingEnabled) {
      const k = clampK(cfg.topKInitial ?? 20);
      const toLocalSnapshots = (hits: RetrievalHit[]): RetrievedChunkSnapshot[] =>
        hits.map((h) => ({
          number: 0,
          docId: h.docId,
          chunkId: h.chunk.id,
          docName: h.docName,
          page: h.chunk.page,
          section: h.chunk.section,
          text: h.chunk.text,
          score: h.score,
          source: "local",
        }));
      // Les bulles de la barre décident des sources, comme en mode standard —
      // mais le mode FullText ne les coupe pas ici : l'agent lit le document
      // entier par un outil et peut encore chercher ailleurs.
      const agentHybrid = (!!cfg.epflHybridEnabled || lexOnly) && !!app.oidcSession;
      const agentExa = !!cfg.exaWebEnabled;
      const activeDocs = hasDocs ? await documents.getDocuments(app.activeDocIds) : [];
      const agentDeps: UltraDeps = {
        ...(hasDocs
          ? {
              search_local: tracked("local", (q: string) =>
                embedQuery(q, cfg, signal)
                  .then((emb) => retrieve(emb, app.activeDocIds, { k }))
                  .then(toLocalSnapshots),
              ),
              read_document: async (name: string) => {
                const i = app.addActivity({ kind: "read", doc: name });
                const step = () => app.activity[i] as Extract<ActivityStep, { kind: "read" }>;
                const wanted = name.trim().toLowerCase();
                const doc =
                  activeDocs.find((d) => d.name.toLowerCase() === wanted) ??
                  activeDocs.find((d) => d.name.toLowerCase().includes(wanted));
                if (!doc) {
                  step().failed = true;
                  throw new Error(`Document introuvable parmi les documents actifs : ${name}`);
                }
                const snaps = doc.chunks.map<RetrievedChunkSnapshot>((chunk) => ({
                  number: 0,
                  docId: doc.id,
                  chunkId: chunk.id,
                  docName: doc.name,
                  page: chunk.page,
                  section: chunk.section,
                  text: chunk.text,
                  score: 1,
                  source: "local",
                }));
                step().count = snaps.length;
                return snaps;
              },
            }
          : {}),
        // KB EPFL en outil CLIENT : les passages rejoignent le corpus, donc
        // citations [N] cliquables avec surlignage dans la source.
        ...(agentHybrid
          ? { search_epfl: tracked(lexOnly ? "polylex" : "epfl", (q: string) => epflSearch(q, cfg.epflHybridTopK, signal)) }
          : {}),
        ...(agentExa ? { search_web: tracked("web", (q: string) => searchExaWeb(q, cfg.exaWebTopK, signal)) } : {}),
      };

      // Documents glissés dans le chat : dans le texte de la question, comme en
      // mode standard. Les images partent à part (le modèle les lit).
      let question = composeText(trimmed, docs);
      if (bypassRag) {
        question +=
          "\n\n(Mode FullText demandé : lis les documents actifs en entier avec read_document avant de répondre.)";
      }
      // Le prompt système de l'agent vit côté backend : la convention des
      // fichiers téléchargeables voyage avec les questions qui en demandent un.
      const whole = bypassRag ? [] : await docsToReadWhole(trimmed, cfg);
      const revising = whole.length > 0 && asksForRevision(trimmed) && whole.some((d) => d.original);
      if (whole.length > 0) {
        const names = whole.map((d) => `« ${d.name} »`).join(", ");
        question += revising
          ? `\n\n(Consigne de l'interface) Modification d'un document demandée : lis en entier avec read_document ${names} avant de proposer les modifications, pour n'oublier aucun passage.`
          : `\n\n(Consigne de l'interface) Les documents interrogés tiennent dans ton contexte : lis ${names} en entier avec read_document avant de répondre, plutôt que par passages.`;
      }
      if (editNote) question += `\n\n${editNote}`;
      // editNote : modification d'un Word ou PDF fourni, même joint au chat ou
      // trop long pour être lu en entier — sans la règle, l'agent le réécrivait.
      // Un fichier déjà produit dans la conversation : « change la signature »
      // ne parle pas de fichier, mais se fait par une révision JSON.
      if (asksForFile(trimmed) || revising || editNote || conversationFileBlocks().length > 0) {
        const sender = senderContext();
        question += `\n\n(Consigne de l'interface)\n${FILE_BLOCK_RULE}${sender ? `\n\n${sender}` : ""}`;
      }
      if (app.askEpfl) {
        question += `\n\n(Consigne de l'interface)\n${askAgentInstructions(cfg.askProfile, cfg.personalInstruction, scopeLabel(cfg, hasDocs))}`;
        const flows = app.isSignedInAs(...DATA_FLOW_USERS) ? askAgentDataFlowRules(cfg.dataFlowMode, trimmed) : "";
        if (flows) question += `\n\n(Consigne de l'interface — schémas)\n${flows}`;
      }
      // En dernier : la langue prime sur les consignes en français qui précèdent.
      const languageRule = answerLanguageRule(cfg.answerLanguage, trimmed);
      if (languageRule) question += `\n\n(Consigne de l'interface)\n${languageRule}`;

      app.pendingAssistant = "";
      // Rien n'a commencé : « Préparation… » jusqu'au premier signe de l'agent
      // (réflexion, recherche ou texte), qui pose la phase.
      app.retrievalPhase = null;
      try {
        const out = await runUltraChat({
          question,
          history: priorMessages.map((m) => ({
            role: m.role === "assistant" ? ("assistant" as const) : ("user" as const),
            content: toAgentText(m),
          })),
          images,
          documentNames: activeDocs.map((d) => d.name),
          backendUrl: cfg.ultraBackendUrl,
          thinking: true,
          // Même budget qu'en mode Rapide : un document lu en entier là l'est ici aussi.
          readDocumentMaxChars: fullTextBudget(cfg) * 4,
          deps: agentDeps,
          signal,
          callbacks: {
            onDelta: (t) => {
              full += t;
              app.touch();
              app.pendingAssistant = full;
              app.retrievalPhase = "answering";
            },
            onCorpus: (snaps) => {
              retrieved = snaps;
              app.pendingRetrieved = snaps;
            },
            onSearching: () => {
              app.touch();
              app.retrievalPhase = "searching";
            },
            onThinking: (t) => app.appendThinking(t),
            onTool: (name) => {
              app.addActivity({ kind: "tool", name });
            },
          },
        });
        if (!full && out.full) full = out.full;
        retrieved = out.retrieved;
      } catch (e) {
        if (isAbortError(e)) {
          aborted = true;
        } else if (e instanceof AgentUnavailableError && !full) {
          agentFailed = true;
          app.addActivity({
            kind: "notice",
            text: tr({
              fr: "Agent indisponible — recherche standard, avec réflexion.",
              en: "Agent unavailable — standard search, with thinking.",
            }),
          });
          console.warn("[agent]", e.message);
        } else {
          throw e;
        }
      }
      if (!agentFailed) {
        if (!aborted) full = await verifySigners(await verifyDocxRevisions(full, cfg, signal), trimmed, cfg, signal);
        return; // le finally partagé persiste la réponse + reset les stores
      }
    }

    if (bypassRag) {
      // Cross-document comparison mode: ship every chunk of every active doc
      // to the LLM, in document/page order, so it can reason holistically
      // instead of over a 10-chunk RAG sample. Citations still work because
      // each chunk keeps its docId/page/section.
      const docs = await documents.getDocuments(app.activeDocIds);
      const snapshots: RetrievedChunkSnapshot[] = [];
      let n = 1;
      for (const doc of docs) {
        for (const chunk of doc.chunks) {
          snapshots.push({
            number: n++,
            docId: doc.id,
            chunkId: chunk.id,
            docName: doc.name,
            page: chunk.page,
            section: chunk.section,
            text: chunk.text,
            score: 1,
          });
        }
      }
      retrieved = snapshots;
      app.pendingRetrieved = retrieved;
    } else if (hasDocs || wantHybrid || wantExa) {
      // Documents envoyés en entier (budget de lecture intégrale, ou Word à
      // réviser) ; la recherche par passages porte sur le reste.
      const whole = await docsToReadWhole(trimmed, cfg);
      wholeDocs = whole;
      const wholeSnaps = whole.flatMap(wholeDocSnapshots);
      for (const d of whole) app.addActivity({ kind: "read", doc: d.name, count: d.chunks.length });
      const searchIds = app.activeDocIds.filter((id) => !whole.some((d) => d.id === id));
      // Rien à chercher (documents entiers, sans source EPFL) : pas de
      // reformulation, qui ne sert qu'à la recherche.
      const searching = searchIds.length > 0 || wantHybrid || wantExa;
      const searchQuery = searching ? await rewriteQuery(trimmed, priorMessages, cfg, signal) : trimmed;
      if (searchQuery.trim() !== trimmed) {
        app.addActivity({ kind: "rewrite", query: searchQuery });
      }

      app.retrievalPhase = "embedding";
      const k = clampK(cfg.topKInitial ?? 20);

      // Map local cosine hits to the shared snapshot shape.
      const toLocalSnapshots = (hits: RetrievalHit[]): RetrievedChunkSnapshot[] =>
        hits.map((h) => ({
          number: 0,
          docId: h.docId,
          chunkId: h.chunk.id,
          docName: h.docName,
          page: h.chunk.page,
          section: h.chunk.section,
          text: h.chunk.text,
          score: h.score,
          source: "local",
        }));

      // Une recherche par source activée, en parallèle, puis fusion. Local
      // d'abord (cosinus, source principale), puis EPFL/SN, puis web ; leurs
      // scores ne sont pas comparables, donc pas d'entrelacement — on renumérote
      // pour que les [N] correspondent au prompt. (La recherche en plusieurs
      // passes est le travail de l'agent, Réflexion activée.)
      const none = () => Promise.resolve<RetrievedChunkSnapshot[]>([]);
      const searchLocal =
        searchIds.length > 0
          ? tracked("local", (q) =>
              embedQuery(q, cfg, signal)
                .then((emb) => retrieve(emb, searchIds, { k }))
                .then(toLocalSnapshots),
            )
          : none;
      const searchEpfl = wantHybrid
        ? tracked(lexOnly ? "polylex" : "epfl", (q) => epflSearch(q, cfg.epflHybridTopK, signal))
        : none;
      const searchWeb = wantExa
        ? tracked("web", (q) => searchExaWeb(q, cfg.exaWebTopK, signal))
        : none;
      const [local, hybrid, exa] = await Promise.all([
        searchLocal(searchQuery),
        searchEpfl(searchQuery),
        searchWeb(searchQuery),
      ]);
      retrieved = [...wholeSnaps, ...local, ...hybrid, ...exa].map((s, i) => ({
        ...s,
        number: i + 1,
      }));
      app.pendingRetrieved = retrieved;
    }

    app.retrievalPhase = "answering";
    app.pendingAssistant = "";

    const historyMessages = priorMessages.map<ChatMessage>((m) => ({
      role: m.role,
      content: toLLMContent(m),
    }));
    // RAG mode whenever we have sources to cite — local docs (even if cosine
    // returned nothing, the doc context is intended) or EPFL/ServiceNow hybrid
    // hits in pure chat-libre. Otherwise fall back to the free-chat prompt.
    const useRag = hasDocs || retrieved.length > 0;
    const baseSystem = useRag
      ? buildSystemPrompt({ dataFlowMode: cfg.dataFlowMode })
      : FREE_SYSTEM_PROMPT;
    const extra = (cfg.extraSystemPrompt ?? "").trim();
    // PolyChat : socle commun + profil + consigne de l'usager. La v1 garde son
    // prompt, suivi de sa consigne modifiable.
    const withExtra = app.askEpfl
      ? buildAskSystemPrompt({
          profile: cfg.askProfile,
          personal: cfg.personalInstruction,
          withPassages: useRag,
          // « Mes documents » sans document : l'usager parle au modèle sans source.
          freeChat: !hasDocs && searchScope(cfg) === "docs",
          scope: scopeLabel(cfg, hasDocs),
          dataFlowMode: app.isSignedInAs(...DATA_FLOW_USERS) ? cfg.dataFlowMode : "none",
        })
      : extra
        ? `${baseSystem}\n\nInstructions supplémentaires :\n${extra}`
        : baseSystem;
    // Langue imposée dans les réglages : en dernier, pour primer sur la règle
    // « langue de la question » du prompt de base.
    const languageRule = answerLanguageRule(cfg.answerLanguage, trimmed);
    const baseWithExtra = languageRule ? `${withExtra}\n\n${languageRule}` : withExtra;
    // What the app can currently reach (which source toggles are on, whether
    // docs are active) — so the model explains "clique sur la bulle EPFL"
    // instead of claiming it has access to nothing.
    const appContext = app.askEpfl
      ? buildAskAppContextBlock({
          scope: searchScope(cfg),
          activeDocCount: app.activeDocIds.length,
          docsRead: !hasDocs ? undefined : bypassRag || wholeDocs.length === app.activeDocIds.length ? "whole" : "passages",
          epflConnected: !!app.oidcSession,
          deep: !!cfg.thinkingEnabled,
        })
      : buildAppContextBlock({
          fullText: app.bypassRag,
          activeDocCount: app.activeDocIds.length,
          epflSearch: !!cfg.epflHybridEnabled,
          lexOnly,
          epflConnected: !!app.oidcSession,
          webSearch: !!cfg.exaWebEnabled,
          thinking: !!cfg.thinkingEnabled,
        });
    const sender = senderContext();
    const systemContent = `${temporal}${sender ? `\n\n${sender}` : ""}\n\n${appContext}\n\n${baseWithExtra}`;
    // The current turn's text is the RAG-augmented prompt when sources are
    // present, otherwise the raw question. Inline attachments (images + dropped
    // docs) are layered on top via composeContent regardless of mode.
    const asked = editNote ? `${trimmed}\n\n${editNote}` : trimmed;
    const currentUserText = useRag ? buildUserPrompt(asked, retrieved) : asked;
    const currentUserContent = composeContent(currentUserText, docs, images);
    const llmMessages: ChatMessage[] = [
      { role: "system", content: systemContent },
      ...historyMessages,
      { role: "user", content: currentUserContent },
    ];

    try {
      for await (const delta of streamChatCompletion(llmMessages, cfg, signal)) {
        if (delta.kind === "thinking") {
          app.appendThinking(delta.text);
        } else {
          full += delta.text;
          app.pendingAssistant = full;
        }
      }
      full = await verifySigners(await verifyDocxRevisions(full, cfg, signal), trimmed, cfg, signal);
    } catch (e) {
      if (isAbortError(e)) {
        aborted = true;
      } else {
        throw e;
      }
    }
  } catch (e) {
    if (isAbortError(e)) {
      aborted = true;
    } else {
      app.reportError(e);
    }
  } finally {
    // Persist whatever was generated, even if the user stopped mid-stream — the
    // partial answer is more useful than throwing it away.
    if (convId && full.length > 0) {
      const content = aborted
        ? `${full}\n\n_${tr({ fr: "[Réponse interrompue par l'utilisateur]", en: "[Answer interrupted by the user]" })}_`
        : full;
      const asstMsg: Message = {
        id: crypto.randomUUID(),
        conversationId: convId,
        role: "assistant",
        content,
        retrievedChunks: retrieved,
        // Copie en objets simples : IndexedDB ne sait pas cloner les proxys
        // $state (cf. la même précaution sur les pièces jointes plus haut).
        ...(app.activity.length > 0 ? { activity: app.activity.map((s) => ({ ...s })) } : {}),
        durationMs: Date.now() - startedAt,
        ...(cfg.thinkingEnabled && !agentFailed ? { deep: true } : {}),
        // Apertus ne rédige qu'en mode Rapide (l'agent garde son modèle).
        ...(cfg.useApertus && !(cfg.thinkingEnabled && !agentFailed) ? { apertus: true } : {}),
        createdAt: Date.now(),
      };
      try {
        await messages.putMessage(asstMsg);
        app.currentMessages = [...app.currentMessages, asstMsg];
        const conv = await conversations.getConversation(convId);
        if (conv) {
          const wasDefault = isDefaultConversationTitle(conv.title);
          conv.updatedAt = Date.now();
          if (wasDefault) {
            // Attachments-only first message has no text to title from — fall
            // back to the doc name or a generic image label.
            const fallback = docs[0]?.name ?? (images.length > 0 ? tr({ fr: "Image jointe", en: "Attached image" }) : "");
            conv.title = titleFromQuestion(trimmed || fallback);
          }
          await conversations.putConversation(conv);
          await app.refreshConversations();
          // PolyChat : un titre court, dans la langue de l'interface, après la
          // première réponse — sauf si l'usager a déjà renommé la conversation,
          // et sauf pour une grille, nommée au lancement d'après la grille.
          const firstAnswer = !app.currentMessages.slice(0, -1).some((m) => m.role === "assistant");
          if (app.askEpfl && firstAnswer && !aborted && trimmed && !app.testSuiteRunning) {
            const provisional = conv.title;
            void generateTitle(trimmed, full, cfg, i18n.locale === "en" ? "en" : "fr").then(async (title) => {
              const latest = title ? await conversations.getConversation(conv.id) : null;
              if (!title || !latest || latest.title !== provisional) return;
              latest.title = title;
              await conversations.putConversation(latest);
              await app.refreshConversations();
            });
          }
        }
      } catch (e) {
        app.lastError = e instanceof Error ? e.message : String(e);
      }
    }
    app.chatAbortController = null;
    app.pendingAssistant = "";
    app.activity = [];
    app.pendingRetrieved = null;
    app.retrievalPhase = null;
    app.isStreaming = false;
  }
}
